import { useDroppable } from '@dnd-kit/core';
import { addMinutes } from 'date-fns';
import { memo, useMemo, useRef } from 'react';

import type { CalendarDroppableData } from '../../hooks/useCalendarDrag';
import { clampRange, isSameDayInTimeZone, startOfDayInTimeZone } from '../../utils/dateGrid';
import { packEvents } from '../../utils/eventLayout';
import { pxToMinutes, snapMinutes } from '../../utils/grid';
import CurrentTimeIndicator from '../CurrentTimeIndicator';
import EventBlock from '../EventBlock';

import type { DayColumnProps } from './types';

const GUTTER_TICK_MINUTES = 60;

function DayColumn({
  day,
  events,
  minMinutes,
  maxMinutes,
  pxPerMinute,
  timeZone,
  snapToMinutes,
  isHighlighted = false,
  showCurrentTimeIndicator = false,
  onEventClick,
  onSlotClick,
  renderEventContent,
  renderEventPopover
}: DayColumnProps) {
  const columnRef = useRef<HTMLDivElement>(null);
  const dayStart = useMemo(() => startOfDayInTimeZone(day, timeZone), [day, timeZone]);
  const windowStart = useMemo(() => addMinutes(dayStart, minMinutes), [dayStart, minMinutes]);
  const windowEnd = useMemo(() => addMinutes(dayStart, maxMinutes), [dayStart, maxMinutes]);

  const droppableData: CalendarDroppableData = { day: dayStart, wholeDay: false };
  const { setNodeRef } = useDroppable({
    id: `day-column-${dayStart.toISOString()}`,
    data: droppableData
  });

  const visibleEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          !event.isAllDay &&
          isSameDayInTimeZone(event.start, event.end, timeZone) &&
          event.start < windowEnd &&
          event.end > windowStart
      ),
    [events, windowStart, windowEnd, timeZone]
  );

  const backgroundEvents = useMemo(
    () => visibleEvents.filter((event) => event.isBackground),
    [visibleEvents]
  );
  const foregroundEvents = useMemo(
    () => visibleEvents.filter((event) => !event.isBackground),
    [visibleEvents]
  );

  const positionsById = useMemo(() => {
    const clipped = foregroundEvents.map((event) => {
      const { start, end } = clampRange(event.start, event.end, windowStart, windowEnd);
      return { ...event, start, end };
    });
    const positioned = packEvents(clipped, { pxPerMinute, dayStart: windowStart, timeZone });
    return new Map(positioned.map((p) => [p.event.id, p]));
  }, [foregroundEvents, pxPerMinute, windowStart, windowEnd, timeZone]);

  const backgroundPositionsById = useMemo(() => {
    const clipped = backgroundEvents.map((event) => {
      const { start, end } = clampRange(event.start, event.end, windowStart, windowEnd);
      return { ...event, start, end };
    });
    const positioned = packEvents(clipped, { pxPerMinute, dayStart: windowStart, timeZone });
    return new Map(positioned.map((p) => [p.event.id, p]));
  }, [backgroundEvents, pxPerMinute, windowStart, windowEnd, timeZone]);

  const handleBackgroundClick = (mouseEvent: React.MouseEvent<HTMLDivElement>) => {
    if (!onSlotClick || !columnRef.current) return;
    if (mouseEvent.target !== mouseEvent.currentTarget) return;
    const rect = columnRef.current.getBoundingClientRect();
    const offsetY = mouseEvent.clientY - rect.top;
    const minutesFromWindowStart = snapMinutes(pxToMinutes(offsetY, pxPerMinute), snapToMinutes);
    const start = addMinutes(windowStart, minutesFromWindowStart);
    const end = addMinutes(start, Math.max(snapToMinutes, 30));
    onSlotClick({ start, end });
  };

  const heightPx = (maxMinutes - minMinutes) * pxPerMinute;
  const hourLineCount = Math.floor((maxMinutes - minMinutes) / GUTTER_TICK_MINUTES);

  return (
    <div
      ref={(node) => {
        columnRef.current = node;
        setNodeRef(node);
      }}
      onClick={handleBackgroundClick}
      className="relative flex-1 border-l border-uniform-gray-100 dark:border-uniform-gray-800"
      style={{ height: heightPx }}>
      {isHighlighted && (
        <div className="pointer-events-none absolute inset-0 bg-uniform-gray-50/70 dark:bg-uniform-gray-800/40" />
      )}

      {Array.from({ length: hourLineCount + 1 }, (_, i) => (
        <div
          key={i}
          className="pointer-events-none absolute inset-x-0 border-t border-uniform-gray-100 dark:border-uniform-gray-800"
          style={{ top: i * GUTTER_TICK_MINUTES * pxPerMinute }}
        />
      ))}

      {backgroundEvents.map((event) => {
        const position = backgroundPositionsById.get(event.id);
        if (!position) return null;
        const widthPct = 100 / position.laneCount;
        return (
          <EventBlock
            key={event.id}
            event={event}
            timeZone={timeZone}
            onClick={onEventClick}
            style={{
              top: position.topPx,
              height: Math.max(position.heightPx, 18),
              left: `${position.laneIndex * widthPct}%`,
              width: `${widthPct}%`,
              zIndex: 0
            }}
          />
        );
      })}

      {foregroundEvents.map((event) => {
        const position = positionsById.get(event.id);
        if (!position) return null;
        const widthPct = 100 / position.laneCount;
        return (
          <EventBlock
            key={event.id}
            event={event}
            timeZone={timeZone}
            onClick={onEventClick}
            renderEventContent={renderEventContent}
            renderEventPopover={renderEventPopover}
            style={{
              top: position.topPx,
              height: Math.max(position.heightPx, 18),
              left: `${position.laneIndex * widthPct}%`,
              width: `${widthPct}%`,
              zIndex: 1
            }}
          />
        );
      })}

      {showCurrentTimeIndicator && (
        <CurrentTimeIndicator
          day={dayStart}
          minMinutes={minMinutes}
          maxMinutes={maxMinutes}
          pxPerMinute={pxPerMinute}
          timeZone={timeZone}
        />
      )}
    </div>
  );
}

export default memo(DayColumn);
